import * as React from "react";
import { ArrowLeft, Loader2, Search, X } from "@/components/icons";
import { Input, type InputSize } from "./input";
import { cn } from "./utils";

interface SearchInputProps
  extends Omit<React.ComponentProps<"input">, "type"> {
  inputSize?: InputSize;
  onClear?: () => void;
  asButton?: boolean;
  onClick?: () => void;
  grey?: boolean;
  loading?: boolean;
  onBack?: () => void;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value, onClear, inputSize, asButton, onClick, grey, loading, onBack, ...props }, ref) => {
    const showClear = onClear && value;

    const input = (
      <Input
        ref={ref}
        type="text"
        round
        inputSize={inputSize}
        value={value}
        readOnly={asButton || props.readOnly}
        tabIndex={asButton ? -1 : props.tabIndex}
        className={cn(
          "border-border-L1 gap-1 px-4 h-11",
          grey ? "bg-surface-L2" : "card-shadow",
          asButton && "cursor-pointer caret-transparent pointer-events-none",
          className,
        )}
        decorationLeft={
          loading ? (
            <Loader2 className="size-5 text-content-primary-subtle animate-spin" />
          ) : onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="p-0.5 hover:opacity-70 transition-opacity"
            >
              <ArrowLeft className="size-5 text-content-primary-subtle" />
            </button>
          ) : (
            <Search className="size-5 text-content-primary-subtle" />
          )
        }
        decorationRight={
          showClear ? (
            <button
              type="button"
              onClick={onClear}
              className="p-0.5 hover:opacity-70 transition-opacity"
            >
              <X className="size-4 text-content-primary-subtle" />
            </button>
          ) : undefined
        }
        {...props}
      />
    );

    if (asButton) {
      return (
        <button
          type="button"
          onClick={onClick}
          className="w-full text-left"
        >
          {input}
        </button>
      );
    }

    return input;
  },
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
